import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Mail, Phone, MapPin, Linkedin, Twitter, 
  Instagram, Youtube, ArrowRight 
} from 'lucide-react';

const footerLinks = {
  products: [
    { name: 'School ERP', href: '/products/school-erp' },
    { name: 'College ERP', href: '/products/college-erp' },
    { name: 'Lynkt', href: '/products/lynkt' },
    { name: 'LMS', href: '/products/lms' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Careers', href: '/careers' },
    { name: 'Press Kit', href: '/press' },
    { name: 'Contact', href: '/contact' },
  ],
  resources: [
    { name: 'Teacher Tools', href: '/teachers' },
    { name: 'Documentation', href: '/docs' },
    { name: 'Blog', href: '/blog' },
    { name: 'Support', href: '/support' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Cookie Policy', href: '/cookies' },
  ],
};

const socialLinks = [];

export const Footer = () => {
  return (
    <footer className="relative bg-void border-t border-glass-border">
      {/* CTA Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal/10 via-void to-orange/10" />
        <div className="absolute inset-0 bg-noise" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
              Let's Create Your
              <span className="text-gradient-teal ml-2">Dream Project</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
              Transform your institution with intelligent campus solutions. 
              Schedule a free consultation today.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-4"
              >
                Schedule a Free Consultation
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="relative w-10 h-10 flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-teal rounded-lg opacity-20" />
                <span className="text-display-xl text-2xl text-teal">T</span>
              </div>
              <div>
                <span className="text-display text-lg text-foreground">Tattva</span>
                <span className="text-display text-lg text-teal ml-1">Techworkz</span>
              </div>
            </Link>
            
            <p className="text-muted-foreground text-sm mb-6 max-w-xs">
              Bridging Deep Tech &amp; Human Potential through the 7 Tattva Framework. 
              Building the future of intelligent education.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href="mailto:info@tattvatechworkz.com"
                className="flex items-center gap-3 text-sm text-muted-foreground hover:text-teal transition-colors"
              >
                <Mail className="w-4 h-4" />
                info@tattvatechworkz.com
              </a>
              <a
                href="tel:+918951964093"
                className="flex items-center gap-3 text-sm text-muted-foreground hover:text-teal transition-colors"
              >
                <Phone className="w-4 h-4" />
                +91 89519 64093
              </a>
              <div className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Bangalore, Karnataka, India</span>
              </div>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-display font-semibold text-foreground mb-4">Products</h3>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-teal transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-display font-semibold text-foreground mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-teal transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-display font-semibold text-foreground mb-4">Resources</h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-teal transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-display font-semibold text-foreground mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-teal transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-glass-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-2 text-sm text-muted-foreground text-center">
              <span className="font-mono text-xs">[ EST. 2025 | PVT. LTD. ]</span>
              <span className="hidden sm:inline">•</span>
              <span>© {new Date().getFullYear()} Tattva Techworkz. All rights reserved.</span>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground hover:text-teal hover:bg-glass-hover transition-all"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
